import { useState, useEffect, useRef } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { 
  Receipt, Calendar, DollarSign, Edit2, Trash2, Save, X, 
  Loader2, Camera, Check, Clock
} from 'lucide-react';
import API from '@/utils/api';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { getFileUrl } from '../../utils/fileStorage';

const ReimbursementDetailSheet = ({ 
  reimbursement, 
  open, 
  onOpenChange, 
  onReimbursementUpdated, 
  onReimbursementDeleted 
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (reimbursement) {
      setFormData({
        description: reimbursement.description || '',
        amount: reimbursement.amount || 0,
        date: reimbursement.date || '',
        receipt_image: reimbursement.receipt_image || null,
        notes: reimbursement.notes || '',
        status: reimbursement.status || 'outstanding',
        date_reimbursed: reimbursement.date_reimbursed || '',
      });
    }
  }, [reimbursement]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image too large (max 5MB)');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      handleChange('receipt_image', event.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!formData.description.trim()) {
      toast.error('Description is required');
      return;
    }
    
    setLoading(true);
    try {
      await API.put(`/reimbursements/${reimbursement.id}`, {
        ...formData,
        amount: parseFloat(formData.amount),
      });
      setIsEditing(false);
      onReimbursementUpdated();
    } catch (error) {
      toast.error('Failed to update reimbursement');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await onReimbursementDeleted();
      setShowDeleteConfirm(false);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkReimbursed = async () => {
    setLoading(true);
    try {
      await API.put(`/reimbursements/${reimbursement.id}`, {
        status: 'reimbursed',
        date_reimbursed: new Date().toISOString().split('T')[0],
      });
      onReimbursementUpdated();
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkOutstanding = async () => {
    setLoading(true);
    try {
      await API.put(`/reimbursements/${reimbursement.id}`, {
        status: 'outstanding',
        date_reimbursed: null,
      });
      onReimbursementUpdated();
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (!reimbursement) return null;

  const isOutstanding = reimbursement.status === 'outstanding';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center justify-between">
            <span>Reimbursement Details</span>
            <div className="flex items-center gap-2">
              {!isEditing ? (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsEditing(true)}
                    data-testid="edit-reimbursement-btn"
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowDeleteConfirm(true)}
                    className="text-red-600 hover:text-red-700"
                    data-testid="delete-reimbursement-btn"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsEditing(false)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleSave}
                    disabled={loading}
                    className="text-green-600"
                    data-testid="save-reimbursement-changes-btn"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                  </Button>
                </>
              )}
            </div>
          </SheetTitle>
        </SheetHeader>

        {/* Delete Confirmation */}
        {showDeleteConfirm && (
          <div className="my-4 p-4 bg-red-50 rounded-lg border border-red-200">
            <p className="text-sm text-red-700 mb-3">
              Are you sure you want to delete this reimbursement?
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleDelete}
                disabled={loading}
                className="bg-red-600 hover:bg-red-700"
              >
                {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Delete
              </Button>
            </div>
          </div>
        )}

        <div className="mt-6 space-y-6">
          {/* Status Badge & Amount */}
          <div className="flex items-center justify-between">
            <Badge className={isOutstanding ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}>
              {isOutstanding ? (
                <>
                  <Clock className="h-3 w-3 mr-1" />
                  Outstanding
                </>
              ) : (
                <>
                  <Check className="h-3 w-3 mr-1" />
                  Reimbursed
                </>
              )}
            </Badge>
            
            {isEditing ? (
              <div className="flex items-center gap-1">
                <span className="text-slate-500">$</span>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.amount}
                  onChange={(e) => handleChange('amount', e.target.value)}
                  className="w-24 text-right text-lg font-bold"
                />
              </div>
            ) : (
              <p className={`text-2xl font-bold ${isOutstanding ? 'text-amber-700' : 'text-green-700'}`}>
                {formatCurrency(reimbursement.amount)}
              </p>
            )}
          </div>

          {/* Status Toggle */}
          {!isEditing && (
            <Button
              variant="outline"
              className={`w-full ${isOutstanding ? 'border-green-200 text-green-700 hover:bg-green-50' : 'border-amber-200 text-amber-700 hover:bg-amber-50'}`}
              onClick={isOutstanding ? handleMarkReimbursed : handleMarkOutstanding}
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : isOutstanding ? (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Mark as Reimbursed
                </>
              ) : (
                <>
                  <Clock className="h-4 w-4 mr-2" />
                  Mark as Outstanding
                </>
              )}
            </Button>
          )}

          {/* Receipt Image */}
          <div className="space-y-2">
            <Label className="text-xs text-slate-500">Receipt</Label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFileSelect}
              className="hidden"
            />
            
            {(reimbursement.receipt_image || formData.receipt_image) ? (
              <div className="relative rounded-lg overflow-hidden border border-slate-200">
                <img 
                  src={isEditing ? (formData.receipt_image?.startsWith('data:') ? formData.receipt_image : getFileUrl(formData.receipt_image)) : getFileUrl(reimbursement.receipt_image)} 
                  alt="Receipt" 
                  className="w-full"
                />
                {isEditing && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute bottom-2 right-2 bg-white/80"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Camera className="h-4 w-4 mr-1" />
                    Change
                  </Button>
                )}
              </div>
            ) : isEditing ? (
              <Button
                type="button"
                variant="outline"
                className="w-full h-24 border-dashed"
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="flex flex-col items-center">
                  <Camera className="h-6 w-6 text-slate-400 mb-1" />
                  <span className="text-sm text-slate-500">Upload Receipt</span>
                </div>
              </Button>
            ) : (
              <div className="w-full h-24 rounded-lg border border-dashed border-slate-200 flex items-center justify-center">
                <div className="text-center">
                  <Receipt className="h-6 w-6 text-slate-300 mx-auto mb-1" />
                  <span className="text-xs text-slate-400">No receipt image</span>
                </div>
              </div>
            )}
          </div>

          {/* Details */}
          <div className="space-y-4">
            {/* Description */}
            <div className="flex items-start gap-3">
              <Receipt className="h-5 w-5 text-slate-400 mt-0.5" />
              <div className="flex-1">
                <Label className="text-xs text-slate-500">Description</Label>
                {isEditing ? (
                  <Input
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    placeholder="Description"
                  />
                ) : (
                  <p className="text-sm text-slate-800">
                    {reimbursement.description}
                  </p>
                )}
              </div>
            </div>

            {/* Date */}
            <div className="flex items-start gap-3">
              <Calendar className="h-5 w-5 text-slate-400 mt-0.5" />
              <div className="flex-1">
                <Label className="text-xs text-slate-500">Date</Label>
                {isEditing ? (
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => handleChange('date', e.target.value)}
                  />
                ) : (
                  <p className="text-sm text-slate-800">
                    {format(parseISO(reimbursement.date), 'MMMM d, yyyy')}
                  </p>
                )}
              </div>
            </div>

            {/* Date Reimbursed (only show if reimbursed) */}
            {!isOutstanding && (
              <div className="flex items-start gap-3">
                <Check className="h-5 w-5 text-green-500 mt-0.5" />
                <div className="flex-1">
                  <Label className="text-xs text-slate-500">Date Reimbursed</Label>
                  {isEditing ? (
                    <Input
                      type="date"
                      value={formData.date_reimbursed}
                      onChange={(e) => handleChange('date_reimbursed', e.target.value)}
                    />
                  ) : (
                    <p className="text-sm text-green-700">
                      {reimbursement.date_reimbursed ? 
                        format(parseISO(reimbursement.date_reimbursed), 'MMMM d, yyyy') :
                        'Not recorded'}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Status (only in edit mode) */}
            {isEditing && (
              <div className="flex items-start gap-3">
                <DollarSign className="h-5 w-5 text-slate-400 mt-0.5" />
                <div className="flex-1">
                  <Label className="text-xs text-slate-500">Status</Label>
                  <select
                    value={formData.status}
                    onChange={(e) => handleChange('status', e.target.value)}
                    className="w-full px-3 py-2 rounded-md border border-slate-200 bg-white text-sm mt-1"
                  >
                    <option value="outstanding">Outstanding</option>
                    <option value="reimbursed">Reimbursed</option>
                  </select>
                </div>
              </div>
            )}

            {/* Notes */}
            {(isEditing || reimbursement.notes) && (
              <div className="pt-4 border-t border-slate-100">
                <Label className="text-xs text-slate-500">Notes</Label>
                {isEditing ? (
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => handleChange('notes', e.target.value)}
                    placeholder="Add notes"
                    rows={3}
                    className="mt-1"
                  />
                ) : (
                  <p className="text-sm text-slate-600 whitespace-pre-wrap mt-1">
                    {reimbursement.notes}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default ReimbursementDetailSheet;
